import { useCallback, type FormEvent } from 'react'
import { emporionToast as toast } from '@admin/toast'
import { requestDeleteSlide, requestReorderSlides, requestUpsertSlide } from '../slidesApi'
import { toSlidePayload } from './helpers'
import type { SlidesCrudArgs } from './useSlidesCrudActions.types'

type MutationArgs = Pick<
  SlidesCrudArgs,
  'authHeaders' | 'editingId' | 'slideForm' | 'setSlides' | 't' | 'requireAuth'
> & {
  loadSlides: () => Promise<void>
  resetSlideForm: () => void
}

export const useSlidesCrudMutationActions = ({
  authHeaders,
  editingId,
  slideForm,
  setSlides,
  t,
  requireAuth,
  loadSlides,
  resetSlideForm,
}: MutationArgs) => {
  const upsertSlide = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      if (!slideForm.title.trim()) {
        toast.error(t('slider.toast.needTitle'))
        return false
      }
      if (!requireAuth('slider.toast.needLogin')) return false
      try {
        await requestUpsertSlide({
          authHeaders,
          editingId,
          payload: toSlidePayload(slideForm),
          t,
        })
        await loadSlides()
        toast.success(editingId ? t('slider.toast.updated') : t('slider.toast.created'))
        resetSlideForm()
        return true
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('slider.toast.saveError'))
        return false
      }
    },
    [authHeaders, editingId, loadSlides, requireAuth, resetSlideForm, slideForm, t],
  )

  const deleteSlide = useCallback(
    async (id: string) => {
      if (!requireAuth('slider.toast.needLogin')) return
      try {
        await requestDeleteSlide({ authHeaders, id, t })
        setSlides((prev) => prev.filter((slide) => slide.id !== id))
        toast.success(t('slider.toast.deleted'))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('slider.toast.deleteError'))
      }
    },
    [authHeaders, requireAuth, setSlides, t],
  )

  const persistOrder = useCallback(
    async (orderIds: string[]) => {
      if (!requireAuth('slider.toast.reorderLogin')) return
      try {
        await requestReorderSlides({ authHeaders, orderIds, t })
        toast.success(t('slider.toast.reordered'))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('slider.toast.reorderError'))
      }
    },
    [authHeaders, requireAuth, t],
  )

  return {
    upsertSlide,
    deleteSlide,
    persistOrder,
  }
}
